import { useTranslation } from "react-i18next";

import Filters from "@/components/Elements/Filters";
import ProgressBar from "@/components/Elements/ProgressBar";
import { REFERENTIAl_STATES_ENDPOINT } from "@/entities/referential/referential-states";
import { useListContext } from "@/modules/list";
import ListTable from "@/modules/list-table";
import ListTableSummary from "@/modules/list-table-summary";
import { ModelLink } from "@/modules/model-link";
import { ModelList } from "@/modules/model-list";
import ProjectBreadcrumb from "@/modules/project/project-breadcrumb/project-breadcrumb.component";
import { ProjectsBatchEdit } from "@/modules/projects/projects-batch-edit";
import { ProjectsListTableRowActions } from "@/modules/projects/projects-list-table";
import { useSidePanel } from "@/modules/side-panel";
import { StateTag } from "@/modules/state-tag";
import {
  getTableFilterQueryable,
  TABLE_FILTER_TEXT,
  useTableFilterReferentialStates,
} from "@/modules/table-filter";
import { TranslateCost } from "@/modules/translate";

import { extractProjectsSummaryInfos } from "./projectsTableUtils";

const ProjectsTable = ({
  showBatchMode = true,
  showLocationColumn = false,
  ...listTableProps
}) => {
  const { t } = useTranslation();
  const { filters } = useListContext();
  const { openSidePanel } = useSidePanel();

  const statesFilter = useTableFilterReferentialStates(
    REFERENTIAl_STATES_ENDPOINT.PROJECT
  );

  const onBatchEditClick = (projects) => {
    openSidePanel({
      title: t("project:form.edit.label"),
      component: <ProjectsBatchEdit projects={projects} />,
    });
  };

  const batchProps = showBatchMode
    ? {
        onBatchEditClick,
      }
    : undefined;

  const columns = [
    {
      title: t("project:form.name.label"),
      dataIndex: "name",
      render: (_name, project) => <ModelLink model="project" item={project} />,
      sorter: true,
      fixed: "left",
      minWidth: 220,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("global:userPlural"),
      dataIndex: ["users", "id"],
      render: (_, { users }) => (
        <ModelList model="user" items={users} withLink />
      ),
      filtersIfCan: "view.user",
      ...getTableFilterQueryable({ model: "users" }),
    },
    {
      title: t("global:location"),
      dataIndex: ["location", "id"],
      render: (_location_id, project) => (
        <ProjectBreadcrumb
          project={project}
          include={["location"]}
          hideIcon
          ellipsis
          compact
        />
      ),
      filtersIfCan: "view.location",
      ...getTableFilterQueryable({ model: "locations", withContext: true }),
      hidden: !showLocationColumn,
    },
    {
      title: t("project:form.reference.label"),
      dataIndex: "reference",
      sorter: true,
      ...TABLE_FILTER_TEXT,
    },
    {
      title: t("project:form.startDate.label"),
      dataIndex: "start_date",
      sorter: true,
    },
    {
      title: t("project:form.dueDate.label"),
      dataIndex: "due_date",
      sorter: true,
    },
    {
      title: t("project:form.progress.label"),
      dataIndex: "progress",
      sorter: true,
      ...Filters.progress,
      render: (progress) => <ProgressBar value={progress} />,
    },
    {
      title: t("global:status"),
      dataIndex: "state",
      render: (state) => <StateTag value={state} type="project" />,
      ...statesFilter,
      filteredValue: filters?.state,
    },
    {
      title: t("project:costsCard.totalCost"),
      dataIndex: "estimated_total_cost",
      align: "right",
      render: (estimated_total_cost) => (
        <TranslateCost value={estimated_total_cost} />
      ),
    },
    {
      title: "",
      fixed: "right",
      render: (project) => <ProjectsListTableRowActions project={project} />,
    },
  ];

  return (
    <ListTable
      columns={columns}
      summary={(projectsData) => {
        const infos = extractProjectsSummaryInfos(projectsData);
        return (
          <ListTableSummary
            label={t("project:list.summaryTotalCost")}
            value={<TranslateCost value={infos.totalCost} />}
            valueAlign="right"
            hasBatchControls={showBatchMode}
            gutterColSpan={showLocationColumn ? 7 : 6}
          />
        );
      }}
      {...batchProps}
      {...listTableProps}
    />
  );
};

export default ProjectsTable;
